/**
 * @fileoverview Present the recorded wait/follow-up context after an Intake information request. The parent supplies monitoring facts; this panel explains the current business state without scheduling timers or delivering outreach. `WorkflowRuntimeSection.js` exposes the underlying durable ledger and `kc-internal-backend/services/intake/workflows/information_request.py` owns managed waiting behavior.
 */

import React from 'react';
import AppIcon from '../../shared/AppIcon';
import { formatDate, pluralize } from '../intakeFormatters';



function formatOptionalDate(value) {
  return value ? formatDate(value) : '';
}



function getInformationRequestMonitoringTone(monitoring = {}) {
  if (!monitoring.has_request) return 'muted';
  if (Number(monitoring.document_count_since_request || 0) > 0 || Number(monitoring.inbound_reply_count_since_request || 0) > 0) return 'success';
  if (monitoring.non_live_delivery) return 'info';
  if (monitoring.enabled === false) return 'warning';
  if (monitoring.latest_poll_status && monitoring.latest_poll_status !== 'success') return 'warning';
  if (monitoring.state === 'polling') return 'active';
  return 'info';
}



function getInformationRequestMonitoringIcon(monitoring = {}) {
  const tone = getInformationRequestMonitoringTone(monitoring);
  if (tone === 'success') return 'pi pi-check-circle';
  if (tone === 'warning') return 'pi pi-exclamation-triangle';
  if (tone === 'active') return 'pi pi-spin pi-spinner';
  if (monitoring.non_live_delivery) return 'pi pi-send';
  return 'pi pi-clock';
}



export function InformationRequestMonitoringPanel({ monitoring }) {
  if (!monitoring?.has_request) return null;
  const tone = getInformationRequestMonitoringTone(monitoring);
  const pollCount = Number(monitoring.poll_count_since_request || 0);
  const documentCount = Number(monitoring.document_count_since_request || 0);
  const replyCount = Number(monitoring.inbound_reply_count_since_request || 0);
  const requestedCount = Number(monitoring.requested_info_count || 0);
  const deliveryLabel = monitoring.delivery_mode_label || 'Non-live send';
  const stats = [
    { label: 'Poll cadence', value: monitoring.non_live_delivery ? 'Not polling' : monitoring.poll_interval_label || monitoring.schedule_expression || 'Not scheduled' },
    { label: 'Poll count', value: pluralize(pollCount, 'poll') },
    { label: 'Documents received', value: pluralize(documentCount, 'document') },
    { label: 'Replies received', value: pluralize(replyCount, 'reply', 'replies') },
  ];
  const detailRows = [
    ['Request sent', formatOptionalDate(monitoring.request_sent_at)],
    ['Delivery mode', monitoring.delivery_mode_label],
    ['Delivered to', monitoring.delivered_recipient_email],
    ['Original recipient', monitoring.original_recipient_email],
    ['Latest poll', formatOptionalDate(monitoring.latest_poll_at)],
    ['Next expected poll', formatOptionalDate(monitoring.next_expected_poll_at)],
    ['No-response review', formatOptionalDate(monitoring.no_response_due_at)],
    ['Mailbox', monitoring.mailbox],
    ['Requested items', requestedCount ? pluralize(requestedCount, 'item') : 'Not itemized'],
  ].filter(([, value]) => value && value !== '-');
  const detail = documentCount || replyCount ?
    'A prospect response has been detected for this request.' :
    monitoring.non_live_delivery ?
      `${deliveryLabel} recorded. Prospect polling starts after a live send.` :
    monitoring.enabled === false ?
      'The reply monitor is not enabled for this environment.' :
      'The reply monitor is checking the intake mailbox for replies and uploaded documents.';

  return (
    <section className={`intake-information-monitor intake-information-monitor--${tone}`} aria-label="Information request response monitor">
      <div className="intake-information-monitor__status">
        <span className="intake-information-monitor__icon">
          <AppIcon icon={getInformationRequestMonitoringIcon(monitoring)} aria-hidden="true" />
        </span>
        <div className="intake-information-monitor__title">
          <strong>{monitoring.status_label || 'Polling for response'}</strong>
          <span>{detail}</span>
        </div>
      </div>
      <div className="intake-information-monitor__stats" aria-label="Response monitor metrics">
        {stats.map((item) =>
          <div className="intake-information-monitor__stat" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        )}
      </div>
      {detailRows.length > 0 &&
        <dl className="intake-information-monitor__details">
          {detailRows.map(([label, value]) =>
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          )}
        </dl>}
    </section>
  );
}
